// ═══════════════════════════════════════════════════════════════════════════════
// Tenant Health Dashboard — At-a-glance operational status of all tenants
//
// Health scoring:
//   🟢 Healthy  — Active, has users, domain OK (if configured)
//   🟡 Warning  — Trial expiring <7d, zero users, or domain not verified
//   🔴 Critical — Suspended/cancelled, trial expired, or SSL failed
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartPulse, Search, ArrowUpRight, Shield, AlertTriangle, XCircle,
  Users, Globe, ChevronDown,
} from 'lucide-react';
import { usePlatformStore } from '@/lib/store';
import { fetchDomain } from '@/lib/api';
import { Spinner, StatusBadge, PlanBadge } from '@/components/shared/UIComponents';
import { cn, formatRelative } from '@/utils/helpers';
import type { Tenant, TenantPlan } from '@/types';

// ── Health types ─────────────────────────────────────────────────────────────

type HealthLevel = 'healthy' | 'warning' | 'critical';

interface TenantHealthInfo {
  tenant: Tenant;
  health: HealthLevel;
  reasons: string[];
  domain: { domain: string; verified: boolean; ssl_status: string } | null;
}

// ── Health scoring logic ─────────────────────────────────────────────────────

function scoreTenantHealth(
  tenant: Tenant,
  domainInfo: { domain: string; verified_at: string | null; ssl_status: string } | null,
): { health: HealthLevel; reasons: string[] } {
  const reasons: string[] = [];

  // Critical checks
  if (tenant.status === 'suspended') {
    reasons.push('Account suspended');
    return { health: 'critical', reasons };
  }
  if (tenant.status === 'cancelled') {
    reasons.push('Account cancelled');
    return { health: 'critical', reasons };
  }

  // Domain / SSL
  if (domainInfo) {
    if (domainInfo.ssl_status === 'failed') {
      reasons.push('SSL certificate failed');
      return { health: 'critical', reasons };
    }
    if (!domainInfo.verified_at) {
      reasons.push('Domain not verified');
    }
    if (domainInfo.ssl_status === 'pending' || domainInfo.ssl_status === 'provisioning') {
      reasons.push(`SSL ${domainInfo.ssl_status}`);
    }
  }

  // Zero users
  if (tenant.current_user_count === 0) {
    reasons.push('No active users');
  }

  // Determine level
  if (reasons.length === 0) return { health: 'healthy', reasons: ['All systems operational'] };
  return { health: 'warning', reasons };
}

// ── Filter type ──────────────────────────────────────────────────────────────

type FilterType = 'all' | 'healthy' | 'warning' | 'critical';

// ═══════════════════════════════════════════════════════════════════════════════

export function TenantHealthPage() {
  const { tenants, loading, loadTenants } = usePlatformStore();
  const [domainMap, setDomainMap] = useState<Map<string, { domain: string; verified_at: string | null; ssl_status: string } | null>>(new Map());
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // Load domain info for tenants that have custom domains
  useEffect(() => {
    if (tenants.length === 0) return;
    const tenantsWithDomains = tenants.filter(t => t.custom_domain);
    if (tenantsWithDomains.length === 0) return;

    setDomainsLoading(true);
    Promise.allSettled(
      tenantsWithDomains.map(async t => {
        const info = await fetchDomain(t.id);
        return { tenantId: t.id, info };
      }),
    ).then(results => {
      const map = new Map<string, any>();
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value.info) {
          map.set(r.value.tenantId, r.value.info);
        }
      });
      setDomainMap(map);
      setDomainsLoading(false);
    });
  }, [tenants]);

  // Score all tenants
  const healthData: TenantHealthInfo[] = useMemo(() => {
    return tenants.map(tenant => {
      const domainInfo = domainMap.get(tenant.id) || null;
      const { health, reasons } = scoreTenantHealth(tenant, domainInfo);
      return {
        tenant,
        health,
        reasons,
        domain: domainInfo ? {
          domain: domainInfo.domain,
          verified: !!domainInfo.verified_at,
          ssl_status: domainInfo.ssl_status,
        } : null,
      };
    });
  }, [tenants, domainMap]);

  // Counts
  const counts = useMemo(() => {
    const c = { healthy: 0, warning: 0, critical: 0 };
    healthData.forEach(h => c[h.health]++);
    return c;
  }, [healthData]);

  // Filtered + searched
  const filtered = useMemo(() => {
    let list = healthData;
    if (filter !== 'all') list = list.filter(h => h.health === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(h =>
        h.tenant.name.toLowerCase().includes(q) ||
        h.tenant.slug.toLowerCase().includes(q) ||
        h.tenant.subdomain.toLowerCase().includes(q),
      );
    }
    // Sort: critical first, then warning, then healthy
    const order: Record<HealthLevel, number> = { critical: 0, warning: 1, healthy: 2 };
    return list.sort((a, b) => order[a.health] - order[b.health]);
  }, [healthData, filter, search]);

  if (loading && tenants.length === 0) {
    return <div className="flex items-center justify-center py-32"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900">Tenant Health</h1>
        <p className="text-surface-500 mt-1">Monitor tenant status and identify issues proactively</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          icon={<Shield size={16} />}
          label="Healthy"
          count={counts.healthy}
          color="emerald"
          active={filter === 'healthy'}
          onClick={() => setFilter(filter === 'healthy' ? 'all' : 'healthy')}
        />
        <SummaryCard
          icon={<AlertTriangle size={16} />}
          label="Warning"
          count={counts.warning}
          color="amber"
          active={filter === 'warning'}
          onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')}
        />
        <SummaryCard
          icon={<XCircle size={16} />}
          label="Critical"
          count={counts.critical}
          color="red"
          active={filter === 'critical'}
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
        />
      </div>

      {/* Search + filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tenants..."
            className="input-field py-1.5 pl-8 text-sm"
          />
        </div>
        {filter !== 'all' && (
          <button
            onClick={() => setFilter('all')}
            className="text-xs text-surface-500 hover:text-surface-700 transition-colors"
          >
            Clear filter
          </button>
        )}
        {domainsLoading && (
          <span className="text-xs text-surface-400 flex items-center gap-1">
            <Spinner size="sm" /> Checking domains...
          </span>
        )}
      </div>

      {/* Tenant list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <HeartPulse size={32} className="mx-auto text-surface-400 mb-3" />
          <p className="text-sm text-surface-500">
            {tenants.length === 0 ? 'No tenants yet.' : 'No tenants match your filter.'}
          </p>
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell">Tenant</th>
                <th className="table-header-cell w-24">Status</th>
                <th className="table-header-cell w-24">Plan</th>
                <th className="table-header-cell w-28">Users</th>
                <th className="table-header-cell w-28">Domain</th>
                <th className="table-header-cell w-24">Health</th>
                <th className="table-header-cell w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <TenantRow
                  key={item.tenant.id}
                  item={item}
                  expanded={expandedId === item.tenant.id}
                  onToggle={() => setExpandedId(expandedId === item.tenant.id ? null : item.tenant.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ icon, label, count, color, active, onClick }: {
  icon: React.ReactNode; label: string; count: number; color: string; active: boolean; onClick: () => void;
}) {
  const styles: Record<string, { bg: string; activeBg: string; text: string; border: string }> = {
    emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/10', activeBg: 'bg-emerald-100 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/30' },
    amber: { bg: 'bg-amber-50 dark:bg-amber-900/10', activeBg: 'bg-amber-100 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30' },
    red: { bg: 'bg-red-50 dark:bg-red-900/10', activeBg: 'bg-red-100 dark:bg-red-900/20', text: 'text-red-600 dark:text-red-400', border: 'border-red-500/30' },
  };
  const s = styles[color] || styles.emerald;

  return (
    <button
      onClick={onClick}
      className={cn(
        'glass-panel p-4 text-left transition-all',
        active && `ring-1 ${s.border}`,
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={cn('p-1.5 rounded-lg', active ? s.activeBg : s.bg, s.text)}>{icon}</div>
        <span className="text-sm text-surface-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-surface-900">{count}</div>
    </button>
  );
}

// ── Tenant Row ───────────────────────────────────────────────────────────────

function TenantRow({ item, expanded, onToggle }: {
  item: TenantHealthInfo; expanded: boolean; onToggle: () => void;
}) {
  const { tenant, health, reasons, domain } = item;
  const plan = tenant.plan as TenantPlan | undefined;
  const userPct = tenant.max_users ? Math.round((tenant.current_user_count / tenant.max_users) * 100) : 0;

  const healthBadge: Record<HealthLevel, { bg: string; text: string; label: string }> = {
    healthy: { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', label: 'Healthy' },
    warning: { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: 'Warning' },
    critical: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Critical' },
  };
  const badge = healthBadge[health];

  return (
    <>
      <tr className="table-row cursor-pointer" onClick={onToggle}>
        <td className="table-cell">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shrink-0 shadow-sm ring-1 ring-slate-500/30">
              <span className="text-white text-xs font-bold">{tenant.name.charAt(0)}</span>
            </div>
            <div>
              <div className="font-medium text-surface-900">{tenant.name}</div>
              <div className="text-xs text-surface-500">{tenant.subdomain}</div>
            </div>
          </div>
        </td>
        <td className="table-cell"><StatusBadge status={tenant.status} /></td>
        <td className="table-cell"><PlanBadge plan={plan?.name || 'free'} label={plan?.display_name} /></td>
        <td className="table-cell">
          <div className="flex items-center gap-2">
            <Users size={12} className="text-surface-400" />
            <span className="text-surface-800">{tenant.current_user_count}</span>
            <span className="text-surface-400">/{tenant.max_users || '∞'}</span>
          </div>
          {tenant.max_users && tenant.max_users > 0 && (
            <div className="mt-1 h-1 w-16 bg-surface-200 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', userPct > 90 ? 'bg-red-500' : userPct > 70 ? 'bg-amber-500' : 'bg-emerald-500')}
                style={{ width: `${Math.min(100, userPct)}%` }}
              />
            </div>
          )}
        </td>
        <td className="table-cell">
          {domain ? (
            <div className="flex items-center gap-1.5">
              <Globe size={12} className={domain.verified ? 'text-emerald-400' : 'text-amber-400'} />
              <span className="text-xs text-surface-600">{domain.ssl_status === 'active' ? 'SSL OK' : domain.ssl_status}</span>
            </div>
          ) : tenant.custom_domain ? (
            <span className="text-xs text-surface-400">Checking...</span>
          ) : (
            <span className="text-xs text-surface-400">— subdomain —</span>
          )}
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium', badge.bg, badge.text)}>
            {badge.label}
          </span>
        </td>
        <td className="table-cell">
          <ChevronDown className={cn('w-3 h-3 text-surface-400 transition-transform', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="px-5 py-4 bg-surface-50 border-b border-surface-200">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-surface-400 block mb-1">Plan</span>
                <span className="text-surface-700 font-medium">{plan?.display_name || plan?.name || 'Free'}</span>
              </div>
              <div>
                <span className="text-surface-400 block mb-1">Created</span>
                <span className="text-surface-700">{formatRelative(tenant.created_at)}</span>
              </div>
              <div>
                <span className="text-surface-400 block mb-1">Storage</span>
                <span className="text-surface-700">{tenant.storage_used_mb || 0} / {tenant.storage_limit_mb} MB</span>
              </div>
            </div>

            {/* Health reasons */}
            <div className="mt-3 flex flex-wrap gap-2">
              {reasons.map((r, i) => (
                <span
                  key={i}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded-full font-medium',
                    health === 'critical' ? 'badge-red' :
                    health === 'warning' ? 'badge-amber' :
                    'badge-emerald',
                  )}
                >
                  {r}
                </span>
              ))}
            </div>

            {/* Quick link */}
            <div className="mt-3">
              <Link
                to={`/admin/tenants/${tenant.id}/manage`}
                className="inline-flex items-center gap-1.5 text-xs text-brand-500 hover:text-brand-600 dark:text-brand-400 font-medium transition-colors"
              >
                Manage Tenant <ArrowUpRight size={12} />
              </Link>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
